import { useState } from "react";
import { View, Button, Text } from "./base";
import { Dialog } from "./base/Dialog";
import { DateInput } from "./base/DateInput";
import { SelectField } from "./base/SelectField";
import { API_URL } from "../config";

type ExportTranscriptsDialogProps = {
  agentId: string;
  isOpen: boolean;
  onClose: () => void;
};

export const ExportTranscriptsDialog = ({ agentId, isOpen, onClose }: ExportTranscriptsDialogProps) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [format, setFormat] = useState<"csv" | "json">("csv");
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!startDate || !endDate) return;

    setIsExporting(true);

    // Convert dates to user's timezone (start of day and end of day)
    const startOfDay = new Date(startDate + "T00:00:00");
    const endOfDay = new Date(endDate + "T23:59:59.999");

    const startDateUtc = startOfDay.toISOString();
    const endDateUtc = endOfDay.toISOString();

    const url = `${API_URL}/v1/agents/${agentId}/sessions/export-transcripts?startDate=${encodeURIComponent(startDateUtc)}&endDate=${encodeURIComponent(endDateUtc)}&format=${format}`;

    if (format === "csv") {
      // Trigger download for CSV
      const link = document.createElement("a");
      link.href = url;
      link.download = `transcripts-${agentId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Open JSON in new tab
      window.open(url, "_blank");
    }

    setIsExporting(false);
    onClose();
  };

  const isValid = startDate && endDate && new Date(startDate) <= new Date(endDate);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Download Transcripts"
      className="w-full max-w-md"
    >
      <View className="space-y-4 p-4">
        <Text className="text-sm text-gray-600 dark:text-gray-400">Download session transcripts for a date range. Maximum 100 sessions per export.</Text>

        <View className="flex gap-4">
          <View className="flex-1">
            <DateInput
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </View>
          <View className="flex-1">
            <DateInput
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </View>
        </View>

        <SelectField
          label="Format"
          value={format}
          onChange={(value) => setFormat(value as "csv" | "json")}
          options={[
            { label: "CSV", value: "csv" },
            { label: "JSON", value: "json" },
          ]}
        />

        <View className="flex justify-end space-x-3 pt-2">
          <Button
            variant="ghost"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={handleExport}
            isLoading={isExporting}
            isDisabled={!isValid || isExporting}
          >
            Export
          </Button>
        </View>
      </View>
    </Dialog>
  );
};
